import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, X, BookOpen, ChevronRight, MessageCircleQuestion } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { getPageHelp, HelpTerm } from '../../config/helpContent';

interface FAQ {
  question: string;
  answer: string;
}

interface ContextualHelpProps {
  className?: string;
  faqs?: FAQ[];
}

/**
 * Componente de ajuda contextual que mostra termos relevantes para a página atual
 */
export function ContextualHelp({ className, faqs }: ContextualHelpProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const location = useLocation();

  const pageHelp = getPageHelp(location.pathname);

  // Se não tiver pageHelp nem faqs, não renderiza
  if (!pageHelp && (!faqs || faqs.length === 0)) {
    return null;
  }

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-40',
          'w-12 h-12 rounded-full',
          'bg-primary-500 hover:bg-primary-400 text-white',
          'shadow-lg shadow-primary-500/25',
          'flex items-center justify-center',
          'transition-all hover:scale-105',
          className
        )}
        title="Ajuda contextual"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      {/* Help Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-96 max-w-full bg-surface-900 border-l border-surface-700 z-50 overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-surface-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Ajuda</h2>
                    <p className="text-xs text-surface-400">{pageHelp?.title || 'Perguntas Frequentes'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg text-surface-400 hover:text-white hover:bg-surface-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Page Description */}
                {pageHelp?.description && (
                  <div className="p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                    <p className="text-sm text-surface-300">{pageHelp.description}</p>
                  </div>
                )}

                {/* Terms */}
                {pageHelp?.terms && pageHelp.terms.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-surface-400 mb-3">
                      Termos desta página
                    </h3>
                    <div className="space-y-2">
                      {pageHelp.terms.map((term) => (
                        <TermCard
                          key={term.term}
                          term={term}
                          isExpanded={expandedTerm === term.term}
                          onToggle={() => setExpandedTerm(
                            expandedTerm === term.term ? null : term.term
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* FAQs */}
                {faqs && faqs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-surface-400 mb-3 flex items-center gap-2">
                      <MessageCircleQuestion className="w-4 h-4" />
                      Perguntas Frequentes
                    </h3>
                    <div className="space-y-2">
                      {faqs.map((faq, index) => (
                        <FaqCard
                          key={index}
                          question={faq.question}
                          answer={faq.answer}
                          isExpanded={expandedFaq === index}
                          onToggle={() => setExpandedFaq(
                            expandedFaq === index ? null : index
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-surface-700">
                <p className="text-xs text-surface-500 text-center">
                  Passe o mouse sobre o ícone <HelpCircle className="w-3 h-3 inline" /> nos campos para ver explicações
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

interface TermCardProps {
  term: HelpTerm;
  isExpanded: boolean;
  onToggle: () => void;
}

function TermCard({ term, isExpanded, onToggle }: TermCardProps) {
  return (
    <div className="rounded-lg border border-surface-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-surface-800 hover:bg-surface-700 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-0.5 bg-primary-500/20 text-primary-400 rounded font-mono">
            {term.term}
          </span>
          <span className="text-sm font-medium text-white">{term.title}</span>
        </div>
        <ChevronRight className={cn(
          'w-4 h-4 text-surface-400 transition-transform',
          isExpanded && 'rotate-90'
        )} />
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-surface-800/50 space-y-3">
              <p className="text-sm text-surface-300">{term.description}</p>
              
              {term.example && (
                <div className="p-2 rounded bg-surface-900 border border-surface-700">
                  <p className="text-xs text-surface-400">
                    <span className="font-medium text-surface-300">Exemplo: </span>
                    {term.example}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface FaqCardProps {
  question: string;
  answer: string;
  isExpanded: boolean;
  onToggle: () => void;
}

function FaqCard({ question, answer, isExpanded, onToggle }: FaqCardProps) {
  return (
    <div className="rounded-lg border border-surface-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-surface-800 hover:bg-surface-700 transition-colors text-left"
      >
        <span className="text-sm font-medium text-white pr-4">{question}</span>
        <ChevronRight className={cn(
          'w-4 h-4 text-surface-400 transition-transform flex-shrink-0',
          isExpanded && 'rotate-90'
        )} />
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-surface-800/50">
              <p className="text-sm text-surface-300">{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ContextualHelp;
